import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Eye, Heart, Upload, DollarSign, Users, TrendingUp, Image, Video, Trash2, Lock, Plus } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CreatorDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentContent, setRecentContent] = useState([]);
  const [activeTab, setActiveTab] = useState('analytics');
  const [uploading, setUploading] = useState(false);
  
  // Content form
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaType, setMediaType] = useState('image');
  const [category, setCategory] = useState('straight');
  const [isPaygated, setIsPaygated] = useState(false);
  const [paygatePrice, setPaygatePrice] = useState('2.99');
  const [tags, setTags] = useState('');

  useEffect(() => {
    if (!user || (user.role !== 'creator' && user.role !== 'admin')) {
      navigate('/');
    }
  }, [user, navigate]);

  const fetchDashboard = useCallback(async () => {
    if (!token) return;
    try {
      const [statsRes, contentRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/dashboard/recent-content`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStats(statsRes.data);
      setRecentContent(contentRes.data.items);
    } catch (err) { console.error(err); }
  }, [token]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handleUpload = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      await axios.post(`${API}/content`, {
        title,
        description,
        media_url: mediaUrl,
        media_type: mediaType,
        category,
        is_paygated: isPaygated,
        paygate_price: isPaygated ? parseFloat(paygatePrice) : null,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setTitle(''); setDescription(''); setMediaUrl(''); setTags('');
      setIsPaygated(false);
      fetchDashboard();
      setActiveTab('analytics');
    } catch (err) { console.error(err); }
    setUploading(false);
  };

  const handleDelete = async (contentId) => {
    try {
      await axios.delete(`${API}/content/${contentId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchDashboard();
    } catch (err) { console.error(err); }
  };

  const statCards = stats ? [
    { label: 'Total Revenue', value: `$${stats.total_revenue.toLocaleString()}`, icon: DollarSign, color: 'cyan' },
    { label: 'Subscribers', value: stats.total_subscribers, icon: Users, color: 'magenta' },
    { label: 'Total Views', value: stats.total_views.toLocaleString(), icon: Eye, color: 'purple' },
    { label: 'Total Likes', value: stats.total_likes.toLocaleString(), icon: Heart, color: 'cyan' },
    { label: 'Content Posts', value: stats.total_content, icon: BarChart3, color: 'magenta' },
  ] : [];

  return (
    <div className="min-h-screen bg-texture" data-testid="creator-dashboard">
      <div className="flex">
        {/* Sidebar */}
        <div className="w-56 min-h-screen border-r border-[--ch-border] bg-[--ch-bg-card] p-4 hidden lg:block" data-testid="dashboard-sidebar">
          <h2 className="text-sm font-bold neon-cyan mb-6 flex items-center gap-2">
            <TrendingUp size={16} /> CREATOR HUB
          </h2>
          <nav className="space-y-1">
            {[
              { id: 'analytics', label: 'Analytics', icon: BarChart3 },
              { id: 'upload', label: 'Upload Content', icon: Upload },
              { id: 'content', label: 'My Content', icon: Image },
            ].map(tab => (
              <button
                key={tab.id}
                data-testid={`sidebar-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                  activeTab === tab.id ? 'bg-[rgba(0,240,255,0.1)] text-[--ch-cyan] font-semibold' : 'text-[--ch-text-secondary] hover:text-white'
                }`}
              >
                <tab.icon size={16} /> {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6">
          {/* Mobile tabs */}
          <div className="flex gap-2 mb-6 lg:hidden overflow-x-auto">
            {['analytics', 'upload', 'content'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg text-xs font-bold whitespace-nowrap ${
                  activeTab === tab ? 'bg-[--ch-cyan] text-black' : 'bg-[--ch-bg-card] text-[--ch-text-secondary]'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="animate-fade-in-up" data-testid="analytics-tab">
              <h2 className="text-2xl font-bold mb-6">Dashboard</h2>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                {statCards.map((s, i) => (
                  <div key={i} className="ch-card p-4" data-testid={`stat-card-${i}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${
                      s.color === 'cyan' ? 'bg-[rgba(0,240,255,0.1)] text-[--ch-cyan]' :
                      s.color === 'magenta' ? 'bg-[rgba(255,0,229,0.1)] text-[--ch-magenta]' :
                      'bg-[rgba(139,92,246,0.1)] text-[--ch-purple]'
                    }`}>
                      <s.icon size={16} />
                    </div>
                    <p className="text-xs text-[--ch-text-secondary]">{s.label}</p>
                    <p className="text-xl font-black mt-1">{s.value}</p>
                  </div>
                ))}
              </div>

              {/* Recent Content */}
              <h3 className="text-lg font-bold mb-4">Recent Content</h3>
              <div className="space-y-2">
                {recentContent.map(item => (
                  <div key={item.id} className="ch-card p-4 flex items-center gap-4" data-testid={`recent-content-${item.id}`}>
                    <div className="w-12 h-12 rounded-lg bg-[--ch-bg-secondary] overflow-hidden flex-shrink-0">
                      <img src={item.thumbnail_url || item.media_url} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold truncate">{item.title}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-[--ch-text-secondary] flex items-center gap-1"><Eye size={10} /> {item.views}</span>
                        <span className="text-xs text-[--ch-text-secondary] flex items-center gap-1"><Heart size={10} /> {item.likes}</span>
                        {item.is_paygated && <span className="text-xs text-[--ch-magenta] flex items-center gap-1"><Lock size={10} /> ${item.paygate_price}</span>}
                      </div>
                    </div>
                    <button
                      data-testid={`delete-content-${item.id}`}
                      onClick={() => handleDelete(item.id)}
                      className="text-[--ch-text-secondary] hover:text-[--ch-error] transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Tab */}
          {activeTab === 'upload' && (
            <div className="max-w-2xl animate-fade-in-up" data-testid="upload-tab">
              <h2 className="text-2xl font-bold mb-6">Upload Content</h2>
              <form onSubmit={handleUpload} className="space-y-4">
                <div>
                  <label className="text-xs text-[--ch-text-secondary] mb-1 block">Title</label>
                  <input
                    data-testid="upload-title"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    className="w-full bg-[--ch-bg-secondary] border border-[--ch-border] rounded-lg px-4 py-2.5 text-white focus:border-[--ch-cyan] focus:outline-none"
                    placeholder="Content title"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs text-[--ch-text-secondary] mb-1 block">Description</label>
                  <textarea
                    data-testid="upload-description"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    rows={3}
                    className="w-full bg-[--ch-bg-secondary] border border-[--ch-border] rounded-lg px-4 py-2.5 text-white focus:border-[--ch-cyan] focus:outline-none resize-none"
                    placeholder="Describe your content..."
                  />
                </div>
                <div>
                  <label className="text-xs text-[--ch-text-secondary] mb-1 block">Media URL</label>
                  <input
                    data-testid="upload-media-url"
                    value={mediaUrl}
                    onChange={e => setMediaUrl(e.target.value)}
                    className="w-full bg-[--ch-bg-secondary] border border-[--ch-border] rounded-lg px-4 py-2.5 text-white focus:border-[--ch-cyan] focus:outline-none"
                    placeholder="https://..."
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-[--ch-text-secondary] mb-1 block">Media Type</label>
                    <div className="flex gap-2">
                      <button type="button" data-testid="upload-type-image"
                        onClick={() => setMediaType('image')}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all flex items-center justify-center gap-1 ${mediaType === 'image' ? 'border-[--ch-cyan] text-[--ch-cyan] bg-[rgba(0,240,255,0.1)]' : 'border-[--ch-border] text-[--ch-text-secondary]'}`}>
                        <Image size={14} /> Image
                      </button>
                      <button type="button" data-testid="upload-type-video"
                        onClick={() => setMediaType('video')}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all flex items-center justify-center gap-1 ${mediaType === 'video' ? 'border-[--ch-magenta] text-[--ch-magenta] bg-[rgba(255,0,229,0.1)]' : 'border-[--ch-border] text-[--ch-text-secondary]'}`}>
                        <Video size={14} /> Video
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-[--ch-text-secondary] mb-1 block">Category</label>
                    <select
                      data-testid="upload-category"
                      value={category}
                      onChange={e => setCategory(e.target.value)}
                      className="w-full bg-[--ch-bg-secondary] border border-[--ch-border] rounded-lg px-4 py-2.5 text-white focus:border-[--ch-cyan] focus:outline-none"
                    >
                      <option value="straight">Straight</option>
                      <option value="gay">Gay</option>
                      <option value="bi">Bi</option>
                      <option value="trans">Trans</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[--ch-text-secondary] mb-1 block">Tags (comma separated)</label>
                  <input
                    data-testid="upload-tags"
                    value={tags}
                    onChange={e => setTags(e.target.value)}
                    className="w-full bg-[--ch-bg-secondary] border border-[--ch-border] rounded-lg px-4 py-2.5 text-white focus:border-[--ch-cyan] focus:outline-none"
                    placeholder="exclusive, premium, behind-the-scenes"
                  />
                </div>
                <div className="ch-card p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Lock size={16} className="text-[--ch-magenta]" />
                      <span className="text-sm font-bold">Pay-Gate Content</span>
                    </div>
                    <button
                      type="button"
                      data-testid="upload-paygate-toggle"
                      onClick={() => setIsPaygated(!isPaygated)}
                      className={`w-10 h-5 rounded-full transition-all relative ${isPaygated ? 'bg-[--ch-magenta]' : 'bg-[--ch-border]'}`}
                    >
                      <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all ${isPaygated ? 'left-5' : 'left-0.5'}`} />
                    </button>
                  </div>
                  {isPaygated && (
                    <div>
                      <label className="text-xs text-[--ch-text-secondary] mb-1 block">Price ($)</label>
                      <input
                        data-testid="upload-paygate-price"
                        type="number"
                        step="0.01"
                        min="0.50"
                        value={paygatePrice}
                        onChange={e => setPaygatePrice(e.target.value)}
                        className="w-full bg-[--ch-bg-secondary] border border-[--ch-border] rounded-lg px-4 py-2.5 text-white focus:border-[--ch-cyan] focus:outline-none"
                      />
                    </div>
                  )}
                </div>
                <button
                  data-testid="upload-submit-btn"
                  type="submit"
                  disabled={uploading}
                  className="w-full btn-neon py-3 rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Upload size={16} />
                  {uploading ? 'Publishing...' : 'Publish Content'}
                </button>
              </form>
            </div>
          )}

          {/* Content Management Tab */}
          {activeTab === 'content' && (
            <div className="animate-fade-in-up" data-testid="content-tab">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">My Content</h2>
                <button
                  data-testid="new-content-btn"
                  onClick={() => setActiveTab('upload')}
                  className="btn-neon px-4 py-2 rounded-lg text-sm flex items-center gap-1"
                >
                  <Plus size={14} /> New
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {recentContent.map(item => (
                  <div key={item.id} className="ch-card overflow-hidden" data-testid={`manage-content-${item.id}`}>
                    <div className="aspect-square bg-[--ch-bg-secondary] overflow-hidden">
                      <img src={item.thumbnail_url || item.media_url} alt={item.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-bold truncate">{item.title}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-[--ch-text-secondary]">{item.views} views</span>
                        <button
                          data-testid={`delete-btn-${item.id}`}
                          onClick={() => handleDelete(item.id)}
                          className="text-[--ch-text-secondary] hover:text-[--ch-error] transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
